/**
 * Root Cause Themes report tab.
 *
 * Starts theme-generation runs and lists run history. Results live on the
 * per-run details page (/reports/themes/:jobId, Cloudscape details-page
 * pattern): starting a run navigates there, and each history row links there.
 */
import { useCallback, useEffect, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import DatePicker from '@cloudscape-design/components/date-picker';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import Multiselect from '@cloudscape-design/components/multiselect';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import { useNavigate } from 'react-router-dom';
import { cancelThemeJob, getFilters, getThemeJob, listThemeJobs, startThemeJob } from '../../api/tickets';
import { ThemeJobSummary } from '../../types/ticket';
import { recordEvent } from '../../rum';
import { CoverageIndicator } from '../CoverageIndicator';
import {
  StatusBadge,
  absoluteTime,
  isActive,
  severityLabel,
  themesToCsv,
  timeAgo,
  windowText,
} from './themeRunShared';

const ALL_GROUPS = '__all__';

const SEVERITY_OPTIONS = [
  { label: 'SEV 1', value: 'SEV_1' },
  { label: 'SEV 2', value: 'SEV_2' },
  { label: 'SEV 2.5', value: 'SEV_2.5' },
  { label: 'SEV 3', value: 'SEV_3' },
  { label: 'SEV 4', value: 'SEV_4' },
  { label: 'SEV 5', value: 'SEV_5' },
];

function daysAgoIso(days: number): string {
  return new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
}

export function RootCauseThemesReport() {
  const navigate = useNavigate();
  const [groupOptions, setGroupOptions] = useState([{ label: 'All resolver groups', value: ALL_GROUPS }]);
  const [group, setGroup] = useState({ label: 'All resolver groups', value: ALL_GROUPS });
  const [dateFrom, setDateFrom] = useState(daysAgoIso(90));
  const [dateTo, setDateTo] = useState(daysAgoIso(0));
  const [severities, setSeverities] = useState<{ label: string; value: string }[]>([]);

  const [runs, setRuns] = useState<ThemeJobSummary[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const { jobs } = await listThemeJobs();
      setRuns(jobs);
    } catch {
      // history is non-critical; leave the current list in place
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => {
    getFilters()
      .then(({ filters }) => {
        const groups: { value: string }[] = filters?.assignedGroups ?? [];
        setGroupOptions([
          { label: 'All resolver groups', value: ALL_GROUPS },
          ...groups.map((g) => ({ label: g.value, value: g.value })),
        ]);
      })
      .catch(() => undefined);
    loadRuns();
  }, [loadRuns]);

  const handleRun = async () => {
    setError(null);
    if (!dateFrom || !dateTo) {
      setError('Select both a start and end date.');
      return;
    }
    if (dateFrom > dateTo) {
      setError('Start date must be on or before end date.');
      return;
    }
    setStarting(true);
    recordEvent('theme_job_start', { group: group.value, dateFrom, dateTo });
    try {
      const params = {
        dateFrom,
        dateTo,
        ...(group.value !== ALL_GROUPS ? { assignedGroup: group.value } : {}),
        ...(severities.length ? { severities: severities.map((s) => s.value) } : {}),
      };
      const { jobId } = await startThemeJob(params);
      navigate(`/reports/themes/${jobId}`);
    } catch (e: any) {
      setStarting(false);
      setError(e.message);
    }
  };

  const handleCancel = async (jobId: string) => {
    setError(null);
    recordEvent('theme_job_cancel', { jobId });
    try {
      await cancelThemeJob(jobId);
      loadRuns();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleDownloadRun = async (jobId: string) => {
    try {
      themesToCsv(await getThemeJob(jobId));
    } catch (e: any) {
      setError(e.message);
    }
  };

  const RUN_COLUMNS = [
    {
      id: 'runId',
      header: 'Run ID',
      width: 120,
      cell: (r: ThemeJobSummary) => (
        <Link
          fontSize="inherit"
          href={`/reports/themes/${r.jobId}`}
          onFollow={(e) => { e.preventDefault(); navigate(`/reports/themes/${r.jobId}`); }}
        >
          {r.jobId.slice(0, 8)}
        </Link>
      ),
    },
    { id: 'status', header: 'Status', width: 130, cell: (r: ThemeJobSummary) => <StatusBadge status={r.status} /> },
    {
      id: 'group',
      header: 'Resolver group',
      cell: (r: ThemeJobSummary) => r.params?.assignedGroup || 'All groups',
    },
    { id: 'window', header: 'Time window', cell: (r: ThemeJobSummary) => windowText(r.params) },
    {
      id: 'severities',
      header: 'Severities',
      cell: (r: ThemeJobSummary) =>
        r.params?.severities?.length ? (
          <SpaceBetween direction="horizontal" size="xxs">
            {r.params.severities.map((s) => (
              <Badge key={s} color="grey">{severityLabel(s)}</Badge>
            ))}
          </SpaceBetween>
        ) : (
          'All'
        ),
    },
    {
      id: 'started',
      header: 'Started',
      cell: (r: ThemeJobSummary) => <span title={timeAgo(r.createdAt)}>{absoluteTime(r.createdAt)}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      width: 100,
      cell: (r: ThemeJobSummary) => (
        <ButtonDropdown
          variant="icon"
          ariaLabel={`Actions for run ${r.jobId.slice(0, 8)}`}
          expandToViewport
          items={[
            { id: 'cancel', text: 'Cancel run', disabled: !isActive(r.status) },
            {
              id: 'csv',
              text: 'Download CSV',
              disabled: !(r.status === 'SUCCEEDED' && r.themeCount > 0),
            },
          ]}
          onItemClick={({ detail }) => {
            if (detail.id === 'cancel') handleCancel(r.jobId);
            else if (detail.id === 'csv') handleDownloadRun(r.jobId);
          }}
        />
      ),
    },
  ];

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description="On-demand AI analysis that clusters a resolver group's tickets into specific, actionable root causes over a time window. Runs as a background job and opens the run's details page."
          >
            Root Cause Themes
          </Header>
        }
      >
        <SpaceBetween size="l">
          <ColumnLayout columns={4}>
            <FormField label="Resolver group">
              <Select selectedOption={group} options={groupOptions} onChange={({ detail }) => setGroup(detail.selectedOption as any)} disabled={starting} />
            </FormField>
            <FormField label="From">
              <DatePicker value={dateFrom} onChange={({ detail }) => setDateFrom(detail.value)} placeholder="YYYY/MM/DD" disabled={starting} />
            </FormField>
            <FormField label="To">
              <DatePicker value={dateTo} onChange={({ detail }) => setDateTo(detail.value)} placeholder="YYYY/MM/DD" disabled={starting} />
            </FormField>
            <FormField label="Severities (optional)">
              <Multiselect selectedOptions={severities} options={SEVERITY_OPTIONS} onChange={({ detail }) => setSeverities(detail.selectedOptions as any)} placeholder="All severities" disabled={starting} />
            </FormField>
          </ColumnLayout>

          <Button variant="primary" onClick={handleRun} loading={starting} disabled={starting}>
            Generate themes
          </Button>

          <CoverageIndicator />
          {error && <Alert type="error">{error}</Alert>}
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header
            variant="h2"
            description="Recent theme runs. Choose a run ID to view its results."
            actions={
              <Button iconName="refresh" ariaLabel="Refresh runs" onClick={loadRuns} loading={runsLoading} />
            }
          >
            Previous runs
          </Header>
        }
      >
        <Table
          items={runs}
          columnDefinitions={RUN_COLUMNS}
          variant="embedded"
          trackBy="jobId"
          loading={runsLoading && runs.length === 0}
          loadingText="Loading runs"
          empty={<Box color="text-body-secondary" padding="l" textAlign="center">No theme runs yet.</Box>}
        />
      </Container>
    </SpaceBetween>
  );
}
